import PropTypes from 'prop-types';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { styled } from '../theming';
import merge from 'lodash/merge';
import { font } from '../utils';
import { fallbackTheme } from '../theming/fallbackTheme';

import { responsiveProp, responsivePropType } from '../utils/responsiveProp';
import { propTypeFontSize } from '../utils/fontSizeProps';
import type { TextProps } from './types';
import { getResponsiveCapsizes } from './utils';

/**
 *
 * @deprecated use [ShortText](https://grammar-designsystem.supernova-docs.io/latest/c/design-foundation/typography/guidelines-A4aJGtUE#section-short-text-e5)
 * or [BodyText](https://grammar-designsystem.supernova-docs.io/latest/c/design-foundation/typography/guidelines-A4aJGtUE#section-body-text-04) instead
 */
export const Text = styled.div.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    defaultValidatorFn(prop) &&
    // Prevent the `TextProps` from being added to the DOM
    !['color', 'fontSize', 'fontWeight', 'leading', 'letterSpacing', 'whiteSpace'].includes(prop),
})<TextProps>(({ color, fontSize, fontWeight, leading, letterSpacing, whiteSpace, theme }) => ({
  ...getResponsiveCapsizes(
    merge(
      responsiveProp(fontSize, 'fontSize'),
      responsiveProp(fontWeight, 'fontWeight'),
      responsiveProp(leading, 'lineHeight'),
      responsiveProp(letterSpacing, 'letterSpacing')
    )
  ),
  color: color ?? fallbackTheme(theme)?.color.surface.foreground.primary,
  whiteSpace,
  margin: 0,
}));

Text.defaultProps = {
  fontSize: 'base',
  letterSpacing: 0,
  fontWeight: 400,
  whiteSpace: 'normal',
};

Text.propTypes = {
  fontSize: responsivePropType(propTypeFontSize),
  leading: responsivePropType(propTypeFontSize),
  letterSpacing: responsivePropType(PropTypes.number),
  fontWeight: PropTypes.oneOf([200, 400, 500, 600, 700]),
  whiteSpace: PropTypes.oneOf(['normal', 'nowrap']),
  color: PropTypes.string,
};

/**
 * This is a wrapper component for outputting text. It cuts the leading and trailing space that the browser adds by default and aligns text vertically by baseline.
 *  * @deprecated use [Title](https://grammar-designsystem.supernova-docs.io/latest/c/design-foundation/typography/guidelines-A4aJGtUE#section-title-c6) instead
 */
export const Header = {
  h4: styled.h4`
    ${({ theme, color }) => ({
      color: color ?? fallbackTheme(theme).color.surface.foreground.primary,
      fontWeight: 500,
      margin: 0,
      ...font({
        type: 'expressive',
        fontSize: tokens.size.font.base,
        lineHeight: tokens.size.font.base,
      }),
    })}
  `,
  h3: styled.h3`
    ${({ theme, color }) => ({
      color: color ?? fallbackTheme(theme).color.surface.foreground.primary,
      fontWeight: 500,
      margin: 0,
      ...font({
        type: 'expressive',
        fontSize: tokens.size.font.big,
        lineHeight: tokens.size.font.big,
      }),
    })}
  `,
  h2: styled.h2`
    ${({ theme, color }) => ({
      color: color ?? fallbackTheme(theme).color.surface.foreground.primary,
      fontWeight: 500,
      margin: 0,
      ...font({
        type: 'expressive',
        fontSize: tokens.size.font.large,
        lineHeight: tokens.size.font.large,
      }),
    })}
  `,
  h1: styled.h1`
    ${({ theme, color }) => ({
      color: color ?? fallbackTheme(theme).color.surface.foreground.primary,
      fontWeight: 500,
      margin: 0,
      ...font({
        type: 'expressive',
        fontSize: tokens.size.font.grand,
        lineHeight: tokens.size.font.grand,
      }),
    })}
  `,
};
